import {
  DEFAULT_CURRENCY_CODE,
  displayCurrencyPlaceholder,
  formatCurrency,
  getTotalLineItemPrice,
} from '@/utils/money';
import { InboundListItem, OrderMetadata } from '@/screens/order-inbounds/types';
import { InputToQuantityMap } from '@/hooks/use-order-inbound-reducer';
import { SmartOrderProcessingStatus, SmartOrderStatus } from '@/enums';
import { useMaybePaymentsContext } from '@/contexts/payments-context';
import { useMemo } from 'react';
import { useNewOrderContext } from '@/contexts/new-order-context';
import { useSupplierDynamicConfigFlag } from '@/hooks/use-dynamic-config-value';

interface Params {
  // Keyed by search input UUID. Only matched inputs are in this map, so
  // unmatched / no-result lines are excluded from the summary for free.
  inputToQuantityMap: InputToQuantityMap;
  // The adapted list items rendered in the panel, so the roll-up reads the same
  // price metadata as the per-line extended prices.
  listItems: InboundListItem[];
  orderMetadata: OrderMetadata[];
}

export interface InboundOrderSummary {
  isVisible: boolean;
  skuCount: number;
  // Pre-formatted subtotal, or null when prices are hidden for this user
  subtotalDisplay: string | null;
  unitCount: number;
}

export const useInboundOrderSummary = ({
  inputToQuantityMap,
  listItems,
  orderMetadata,
}: Params): InboundOrderSummary => {
  const isInboundHistoryEnabled = useSupplierDynamicConfigFlag(
    'smart_order_config',
    'inbound_history_enabled',
  );
  const {
    itemDisplayOptions: { displayItemPrices },
  } = useNewOrderContext();
  const paymentsCurrencyCode = useMaybePaymentsContext()?.currencyCode;

  const isPendingAndProcessed = useMemo(
    () =>
      orderMetadata.length > 0 &&
      orderMetadata.every(
        (order) =>
          order.status === SmartOrderStatus.PENDING &&
          order.processingStatus === SmartOrderProcessingStatus.FINISHED,
      ),
    [orderMetadata],
  );

  return useMemo(() => {
    const metadataByInputUUID = new Map(
      listItems
        .filter((item) => item.metadata)
        .map((item) => [
          item.metadata!.inputWithResult.uuid,
          item.metadata!.listOption?.metadata,
        ]),
    );

    let skuCount = 0;
    let packCount = 0;
    let subtotalInMicros = 0;
    let currencyCode: string | null = null;
    let hasUnpricedLine = false;

    for (const searchInputUUID of Object.keys(inputToQuantityMap)) {
      const { quantity } = inputToQuantityMap[searchInputUUID];
      if (quantity <= 0) {
        continue;
      }

      skuCount += 1;
      packCount += quantity;

      const itemMetadata = metadataByInputUUID.get(searchInputUUID);
      currencyCode = currencyCode || itemMetadata?.currencyCode || null;

      const linePriceInMicros = itemMetadata
        ? getTotalLineItemPrice({
            quantity,
            unitCount: itemMetadata.unitCount,
            unitPriceInMicros:
              itemMetadata.promotionPricePerUnitInMicros ??
              itemMetadata.unitPriceInMicros,
          })
        : null;

      if (linePriceInMicros === null) {
        hasUnpricedLine = true;
        continue;
      }

      subtotalInMicros += linePriceInMicros;
    }

    const resolvedCurrencyCode =
      currencyCode || paymentsCurrencyCode || DEFAULT_CURRENCY_CODE;

    return {
      isVisible:
        isInboundHistoryEnabled && isPendingAndProcessed && skuCount > 0,
      skuCount,
      subtotalDisplay: !displayItemPrices
        ? null
        : hasUnpricedLine
          ? displayCurrencyPlaceholder(resolvedCurrencyCode)
          : formatCurrency(subtotalInMicros, resolvedCurrencyCode),
      unitCount: packCount,
    };
  }, [
    displayItemPrices,
    inputToQuantityMap,
    isInboundHistoryEnabled,
    isPendingAndProcessed,
    listItems,
    paymentsCurrencyCode,
  ]);
};
